import React, { useState } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CollapsibleChevron from '@/components/shared/CollapsibleChevron';
import { PersonCard } from '@/components/person/PersonCard';
import ProfileSectionCount from '@/components/person/ProfileSectionCount';
import type { ProfileLocationState } from '@/types/profile-navigation';
import { PET_OWNER_ROLE_LABELS, type PetOwnerRoleValue } from '@/types/pet-owner';
import '../person/person-relationships.css';

export interface PetProfileOwnerLink {
  id: string;
  ownerFamilyMemberId: string;
  role: string;
}

interface PetProfileOwnersProps {
  owners: PetProfileOwnerLink[];
  familyId?: string;
  defaultExpanded?: boolean;
}

const PetProfileOwners: React.FC<PetProfileOwnersProps> = observer(({ owners, familyId, defaultExpanded = true }) => {
  const { t } = useTranslation('forms');
  const navigate = useNavigate();
  const location = useLocation();
  const { familyId: familyIdParam } = useParams<{ familyId: string }>();
  const effectiveFamilyId = familyId ?? familyIdParam ?? '';
  const profileLocationState = location.state as ProfileLocationState | undefined;

  const [expanded, setExpanded] = useState(defaultExpanded);

  const handlePersonProfileClick = (targetFamilyMemberId: string) => {
    navigate(`/families/${effectiveFamilyId}/people/${targetFamilyMemberId}`, {
      state: profileLocationState
    });
  };

  const handleTreeViewClick = (targetFamilyMemberId: string) => {
    navigate(`/families/${effectiveFamilyId}/hybrid-pedigree/${targetFamilyMemberId}`);
  };

  return (
    <Collapsible.Root
      open={expanded}
      onOpenChange={setExpanded}
      className="person-relationships person-relationships--profile"
    >
      <div className="person-relationships__header">
        <Collapsible.Trigger asChild>
          <button type="button" className="person-relationships__header-button">
            <CollapsibleChevron className="person-relationships__chevron" />
            <h2 className="person-relationships__title">{t('pet.owners')}</h2>
            <ProfileSectionCount count={owners.length} />
          </button>
        </Collapsible.Trigger>
      </div>

      <Collapsible.Content>
        <div className="person-relationships__body">
          {owners.length === 0 ? (
            <p className="profile-rels__empty">{t('pet.noOwnersRecorded')}</p>
          ) : (
            <div className="profile-rels profile-rels--pet-owners members-cards-scope">
              <div className="profile-rels__grid members-grid">
                {owners.map((owner) => {
                  const relType = owner.role as PetOwnerRoleValue;
                  return (
                    <div key={owner.id} className="profile-rels__card-wrap">
                      <div className="profile-rels__card-surface">
                        <PersonCard
                          familyMemberId={owner.ownerFamilyMemberId}
                          generation={0}
                          layout="members"
                          isTreeAnchor={false}
                          onTreeViewClick={handleTreeViewClick}
                          onProfileClick={handlePersonProfileClick}
                        />
                        {relType != null && (
                          <span className="profile-rels__link-badge profile-rels__role-badge">
                            {PET_OWNER_ROLE_LABELS[relType] ?? owner.role}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
});

export default PetProfileOwners;
